import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  FiArrowLeft,
  FiSearch,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiFilter,
  FiX,
  FiBarChart2,
} from "react-icons/fi";

import Loader from "../../Global/Loader";
import { UserContext } from "../../../context/UserContext";
import {
  fetchCrmBoardDashboard,
  fetchKanbanBoardDetail,
  fetchAvailableTags,
} from "../../../services/crmApi";
import { fetchSalesPersonsNormalized } from "../../../services/salesPersonApi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const LEAD_SOURCE_OPTIONS = [
  "Facebook",
  "WhatsApp",
  "Instagram",
  "LinkedIn",
  "Email",
  "Referral",
  "Direct",
  "Other",
];

const formatCurrency = (n) => {
  const v = Number(n) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "PKR",
    maximumFractionDigits: v % 1 === 0 ? 0 : 2,
  }).format(v);
};

const formatPercent = (n) => {
  const v = Number(n) || 0;
  return `${v.toFixed(v % 1 === 0 ? 0 : 1)}%`;
};

/** Preset id → compute [dateFrom, dateTo] as ISO strings */
function rangeForPreset(presetId) {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);

  switch (presetId) {
    case "30d":
      start.setDate(start.getDate() - 30);
      break;
    case "90d":
      start.setDate(start.getDate() - 90);
      break;
    case "ytd":
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      break;
    case "12m":
    default:
      start.setFullYear(start.getFullYear() - 1);
      start.setHours(0, 0, 0, 0);
      break;
  }

  return {
    dateFrom: start.toISOString(),
    dateTo: end.toISOString(),
    label:
      presetId === "12m"
        ? "Last 12 Months"
        : presetId === "30d"
          ? "Last 30 Days"
          : presetId === "90d"
            ? "Last 90 Days"
            : "Year to Date",
  };
}

function shiftRange(dateFromIso, dateToIso, direction) {
  const from = new Date(dateFromIso);
  const to = new Date(dateToIso);
  const span = to.getTime() - from.getTime();
  if (span <= 0) return { dateFrom: dateFromIso, dateTo: dateToIso };
  const delta = direction === "next" ? span : -span;
  const nFrom = new Date(from.getTime() + delta);
  const nTo = new Date(to.getTime() + delta);
  return {
    dateFrom: nFrom.toISOString(),
    dateTo: nTo.toISOString(),
  };
}

const SortHeader = ({ label, active, dir, onClick, align = "right" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-0.5 text-xs font-medium text-gray-600 hover:text-teal-900 w-full ${
      align === "right" ? "justify-end" : "justify-start"
    }`}
  >
    <span>{label}</span>
    <FiChevronDown
      className={`shrink-0 transition-transform ${
        active ? (dir === "asc" ? "rotate-180" : "") : "opacity-40"
      }`}
      size={14}
    />
  </button>
);

const TopTable = ({
  title,
  nameHeader,
  rows,
  nameKey,
  getName,
  sort,
  onSort,
  search,
}) => {
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const name = (getName ? getName(r) : r[nameKey]) || "";
      return String(name).toLowerCase().includes(q);
    });
  }, [rows, search, nameKey, getName]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const { key, dir } = sort;
    copy.sort((a, b) => {
      let av;
      let bv;
      if (key === "name") {
        av = (getName ? getName(a) : a[nameKey]) || "";
        bv = (getName ? getName(b) : b[nameKey]) || "";
        return dir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      av = Number(a[key]) || 0;
      bv = Number(b[key]) || 0;
      return dir === "asc" ? av - bv : bv - av;
    });
    return copy;
  }, [filtered, sort, nameKey, getName]);

  const totals = useMemo(() => {
    return sorted.reduce(
      (acc, r) => ({
        leads: acc.leads + (Number(r.leadCount) || 0),
        revenue: acc.revenue + (Number(r.revenue) || 0),
      }),
      { leads: 0, revenue: 0 },
    );
  }, [sorted]);

  const toggle = (key) => {
    onSort((prev) => ({
      key,
      dir: prev.key === key ? (prev.dir === "desc" ? "asc" : "desc") : "desc",
    }));
  };

  return (
    <div className="bg-white rounded-sm border border-gray-200/80 shadow-sm flex flex-col min-h-[220px]">
      <h3 className="text-[15px] font-semibold text-teal-900 px-4 pt-4 pb-2 tracking-tight">
        {title}
      </h3>
      <div className="border-t border-gray-200 mx-4" />
      <div className="px-2 pb-3 pt-2 flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-600">
              <th className="text-left font-medium py-2 px-2">{nameHeader}</th>
              <th className="text-right py-2 px-2 w-[88px]">
                <SortHeader
                  label="# Leads"
                  active={sort.key === "leadCount"}
                  dir={sort.dir}
                  onClick={() => toggle("leadCount")}
                />
              </th>
              <th className="text-right py-2 px-2 w-[100px]">
                <SortHeader
                  label="Revenue"
                  active={sort.key === "revenue"}
                  dir={sort.dir}
                  onClick={() => toggle("revenue")}
                />
              </th>
            </tr>
          </thead>
          <tbody className="text-gray-800">
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="py-12 text-center text-gray-400 text-xs"
                >
                  No data
                </td>
              </tr>
            ) : (
              sorted.map((row, idx) => (
                <tr key={idx} className="border-t border-gray-100">
                  <td className="py-2 px-2 truncate max-w-[140px]">
                    {getName ? getName(row) : row[nameKey]}
                  </td>
                  <td className="text-right py-2 px-2 tabular-nums">
                    {row.leadCount ?? 0}
                  </td>
                  <td className="text-right py-2 px-2 tabular-nums">
                    {formatCurrency(row.revenue ?? 0)}
                  </td>
                </tr>
              ))
            )}
            {sorted.length !== 0 && (
              <tr className="border-t border-gray-200 font-medium bg-gray-50/80">
                <td className="py-2 px-2 text-gray-700">Total</td>
                <td className="text-right py-2 px-2 tabular-nums">
                  {totals.leads}
                </td>
                <td className="text-right py-2 px-2 tabular-nums">
                  {formatCurrency(totals.revenue)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CrmDashboardView = () => {
  const location = useLocation();
  const fromCRM = location.state?.fromCRM;
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { userRole } = useContext(UserContext);
  const isAdmin = userRole?.toLowerCase() === "admin";
  const isConsultant = userRole?.toLowerCase() === "consultant";
  const basePath = isAdmin ? "/admin/crm" : isConsultant ? "/consultant/crm" : "/sales/crm";

  const [loading, setLoading] = useState(true);
  const [boardName, setBoardName] = useState("");
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  const [presetId, setPresetId] = useState("12m");
  const [dateFrom, setDateFrom] = useState(
    () => rangeForPreset("12m").dateFrom,
  );
  const [dateTo, setDateTo] = useState(() => rangeForPreset("12m").dateTo);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [tagsFilter, setTagsFilter] = useState([]);
  const [category, setCategory] = useState("");
  const [salesPerson, setSalesPerson] = useState("");
  const [leadSource, setLeadSource] = useState("");

  const [tagOptions, setTagOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [salesPersonOptions, setSalesPersonOptions] = useState([]);

  const [showFilters, setShowFilters] = useState(false);

  const [tagSort, setTagSort] = useState({
    key: "revenue",
    dir: "desc",
  });
  const [catSort, setCatSort] = useState({
    key: "revenue",
    dir: "desc",
  });
  const [spSort, setSpSort] = useState({
    key: "revenue",
    dir: "desc",
  });
  const [srcSort, setSrcSort] = useState({
    key: "revenue",
    dir: "desc",
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!isAdmin) {
      navigate(basePath, { replace: true });
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!boardId || !isAdmin) return;
    (async () => {
      try {
        const res = await fetchKanbanBoardDetail(boardId);
        setBoardName(res?.data?.name || "");
      } catch (_) {
        setBoardName("");
      }
    })();
  }, [boardId, isAdmin]);

  useEffect(() => {
    if (!boardId || !isAdmin) return;
    (async () => {
      try {
        const tagsRes = await fetchAvailableTags(boardId);
        const raw = tagsRes?.data ?? tagsRes;
        const list = Array.isArray(raw) ? raw : raw?.tags || [];
        setTagOptions(Array.isArray(list) ? list.filter(Boolean) : []);
      } catch (_) {
        setTagOptions([]);
      }
      try {
        const normalized = await fetchSalesPersonsNormalized();
        setSalesPersonOptions(
          normalized.map((item) => ({
            id: String(item._id || ""),
            name: item.name || item.email || "Unknown",
          })),
        );
      } catch (_) {
        setSalesPersonOptions([]);
      }
    })();
  }, [boardId, isAdmin]);

  const loadDashboard = useCallback(async () => {
    if (!boardId || !isAdmin) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetchCrmBoardDashboard(boardId, {
        tags: tagsFilter.length ? tagsFilter : undefined,
        category: category || undefined,
        salesPerson: salesPerson || undefined,
        leadSource: leadSource || undefined,
        dateFrom,
        dateTo,
      });
      const payload = res?.data ?? res;
      setDashboard(payload);

      const tops = payload?.tables;
      if (tops?.topCategories?.length) {
        setCategoryOptions((prev) => {
          const next = new Set(prev);
          tops.topCategories.forEach((c) => c.category && next.add(c.category));
          return Array.from(next).sort();
        });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load dashboard";
      setError(msg);
      if (err?.response?.status === 403) {
        toast.error("You do not have access to this dashboard");
        navigate(basePath, { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [
    boardId,
    isAdmin,
    tagsFilter,
    category,
    salesPerson,
    leadSource,
    dateFrom,
    dateTo,
    navigate,
  ]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const charts = dashboard?.charts;
  const leadsByMonth = charts?.leadsByMonth;
  const tables = dashboard?.tables;
  const kpis = dashboard?.kpis || {};
  const summary = dashboard?.summary;

  const chartData = useMemo(() => {
    const rows = Array.isArray(leadsByMonth) ? leadsByMonth : [];
    const labels = rows.map((m) => m.label || m.month);
    return {
      labels,
      datasets: [
        {
          label: "Leads",
          data: rows.map((m) => m.leadCount ?? 0),
          borderColor: "rgb(13, 148, 136)",
          backgroundColor: "rgba(13, 148, 136, 0.08)",
          tension: 0.25,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: "Won",
          data: rows.map((m) => m.wonCount ?? 0),
          borderColor: "rgb(59, 130, 246)",
          backgroundColor: "transparent",
          tension: 0.25,
          borderDash: [4, 3],
          fill: false,
          pointRadius: 2,
        },
      ],
    };
  }, [leadsByMonth]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "top",
          align: "end",
          labels: { boxWidth: 12, font: { size: 11 } },
        },
        title: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 11 }, color: "#6b7280" },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(0,0,0,0.06)" },
          ticks: {
            font: { size: 11 },
            color: "#6b7280",
            precision: 0,
          },
          title: {
            display: true,
            text: "Leads",
            color: "#6b7280",
            font: { size: 11 },
          },
        },
      },
    }),
    [],
  );

  const presetLabel = useMemo(() => {
    if (presetId === "custom") return "Custom range";
    return rangeForPreset(presetId).label;
  }, [presetId]);

  const applyPreset = (id) => {
    setPresetId(id);
    if (id === "custom") return;
    const r = rangeForPreset(id);
    setDateFrom(r.dateFrom);
    setDateTo(r.dateTo);
  };

  const handleShift = (dir) => {
    const next = shiftRange(dateFrom, dateTo, dir);
    setDateFrom(next.dateFrom);
    setDateTo(next.dateTo);
    setPresetId("custom");
  };

  const toggleTag = (tag) => {
    setTagsFilter((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const clearFilters = () => {
    setTagsFilter([]);
    setCategory("");
    setSalesPerson("");
    setLeadSource("");
    applyPreset("12m");
  };

  const activeFilterCount =
    tagsFilter.length +
    (category ? 1 : 0) +
    (salesPerson ? 1 : 0) +
    (leadSource ? 1 : 0);

  const mergedCategories = useMemo(() => {
    const s = new Set(categoryOptions);
    tables?.topCategories?.forEach((c) => c.category && s.add(c.category));
    return Array.from(s).filter(Boolean).sort();
  }, [categoryOptions, tables?.topCategories]);

  const mergedSources = useMemo(() => {
    const s = new Set(LEAD_SOURCE_OPTIONS);
    tables?.topSources?.forEach((r) => r.source && s.add(r.source));
    return Array.from(s).sort();
  }, [tables?.topSources]);

  if (!isAdmin) return null;

  if (loading && !dashboard) {
    return <Loader />;
  }

  return (
    <div className="min-h-0 bg-[#f4f8fb] flex flex-col pt-16 sm:mt-12 pl-3 pr-3 pb-8 sm:pl-36 sm:pr-8 relative">
      {loading && dashboard && (
        <div className="absolute inset-0 z-10 bg-white/50 flex items-center justify-center rounded-sm">
          <Loader />
        </div>
      )}

      {/* Odoo-style toolbar */}
      <div className="bg-white text-black rounded-sm shadow-md mb-4 flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            type="button"
            onClick={() =>
              navigate(fromCRM ? `${basePath}` : `${basePath}/${boardId}`)
            }
            className="p-2 hover:bg-gray-100 rounded-md shrink-0"
            title="Back to board"
          >
            <FiArrowLeft size={18} />
          </button>
          <span className="text-sm font-medium truncate hidden sm:inline">
            Dashboards
          </span>
        </div>

        <div className="flex-1 min-w-[200px] max-w-xl relative">
          <FiSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 text-black"
            size={16}
          />
          <input
            type="search"
            placeholder="Search in tables..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-md bg-white border border-gray-300 text-sm text-black placeholder:text-black focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`flex items-center hover:bg-gray-100 gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors  ${
              activeFilterCount > 0
                ? "bg-black text-white"
                : "bg-white text-black"
            }`}
          >
            <FiFilter size={16} />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-white text-black text-[10px] rounded-full min-w-[1.25rem] h-5 px-1 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
            <FiChevronDown
              size={14}
              className={`transition-transform ${showFilters ? "rotate-180" : ""}`}
            />
          </button>

          <div className="flex items-center gap-1 bg-white rounded-md px-2 py-1 border border-gray-300">
            <FiCalendar className="text-black shrink-0" size={16} />
            <select
              value={presetId}
              onChange={(e) => applyPreset(e.target.value)}
              className="bg-transparent text-sm text-black py-1 pr-6 focus:outline-none cursor-pointer max-w-[140px] sm:max-w-none"
            >
              <option value="12m">Last 12 Months</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="ytd">Year to Date</option>
              <option value="custom">Custom range</option>
            </select>
            <button
              type="button"
              onClick={() => handleShift("prev")}
              className="p-1 hover:bg-gray-100 rounded"
              title="Previous period"
            >
              <FiChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => handleShift("next")}
              className="p-1 hover:bg-gray-100 rounded"
              title="Next period"
            >
              <FiChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {presetId === "custom" && (
        <div className="bg-white rounded-md border border-gray-200 p-4 mb-4 flex flex-wrap gap-4 items-end shadow-sm">
          <label className="flex flex-col gap-1 text-xs text-gray-600">
            From
            <input
              type="datetime-local"
              value={dateFrom.slice(0, 16)}
              onChange={(e) =>
                setDateFrom(new Date(e.target.value).toISOString())
              }
              className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-600">
            To
            <input
              type="datetime-local"
              value={dateTo.slice(0, 16)}
              onChange={(e) =>
                setDateTo(new Date(e.target.value).toISOString())
              }
              className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
        </div>
      )}

      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">
              Tags
            </h4>
            <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
              {tagOptions.length === 0 ? (
                <span className="text-xs text-gray-400">No tags on board</span>
              ) : (
                tagOptions.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                      tagsFilter.includes(tag)
                        ? "bg-teal-100 border-teal-600 text-teal-900"
                        : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {tag}
                  </button>
                ))
              )}
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">
              Category
            </h4>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All categories</option>
              {mergedCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">
              Sales person
            </h4>
            <select
              value={salesPerson}
              onChange={(e) => setSalesPerson(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {salesPersonOptions.map((sp) => (
                <option key={sp.id} value={sp.id}>
                  {sp.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">
              Lead source
            </h4>
            <select
              value={leadSource}
              onChange={(e) => setLeadSource(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All sources</option>
              {mergedSources.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          {activeFilterCount > 0 && (
            <div className="md:col-span-2 lg:col-span-4 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md flex items-center gap-1"
              >
                <FiX size={14} /> Clear filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Title row */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FiBarChart2 className="text-teal-800" />
            CRM Dashboard
            {boardName && (
              <span className="text-gray-500 font-normal text-base">
                — {boardName}
              </span>
            )}
          </h1>
        </div>
        <div className="text-xs text-gray-600 flex flex-wrap gap-3">
          {presetLabel}
          {presetId !== "custom" && (
            <>
              {" "}
              ·{" "}
              {new Date(dateFrom).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}{" "}
              –{" "}
              {new Date(dateTo).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </>
          )}
        </div>
        {summary && (
          <div className="text-xs text-gray-600 flex flex-wrap gap-3">
            <span>
              Total leads: <strong>{summary.totalLeads ?? 0}</strong>
            </span>
            <span>
              Open: <strong>{summary.openLeads ?? 0}</strong>
            </span>
            <span>
              Won: <strong>{summary.wonLeads ?? 0}</strong>
            </span>
            <span>
              Lost: <strong>{summary.lostLeads ?? 0}</strong>
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md mb-4 text-sm">
          {error}
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-lg shadow-sm border border-blue-100 bg-sky-50 p-5 flex flex-col items-center justify-center min-h-[120px]">
          <span className="text-xs font-medium text-blue-800/80 uppercase tracking-wide">
            Close Rate
          </span>
          <span className="text-3xl font-semibold text-blue-950 mt-2 tabular-nums">
            {formatPercent(kpis.closeRate?.value ?? 0)}
          </span>
          <span className="text-[11px] text-blue-700/70 mt-2">
            Won / (Won + Lost)
          </span>
        </div>
        <div className="rounded-lg shadow-sm border border-orange-100 bg-orange-50/90 p-5 flex flex-col items-center justify-center min-h-[120px]">
          <span className="text-xs font-medium text-orange-900/80 uppercase tracking-wide">
            Average Deal Size
          </span>
          <span className="text-3xl font-semibold text-orange-950 mt-2 tabular-nums">
            {formatCurrency(kpis.averageDealSize?.value ?? 0)}
          </span>
          <span className="text-[11px] text-orange-800/70 mt-2">
            Revenue ÷ won leads
          </span>
        </div>
        <div className="rounded-lg shadow-sm border border-amber-100 bg-amber-50 p-5 flex flex-col items-center justify-center min-h-[120px]">
          <span className="text-xs font-medium text-amber-900/80 uppercase tracking-wide">
            Revenue
          </span>
          <span className="text-3xl font-semibold text-amber-950 mt-2 tabular-nums">
            {formatCurrency(kpis.revenue?.value ?? 0)}
          </span>
          <span className="text-[11px] text-amber-900/70 mt-2">
            Sum of expected deal revenue (won)
          </span>
        </div>
      </div>

      {/* Leads by month */}
      <div className="bg-white rounded-sm border border-gray-200 shadow-sm p-4 mb-6">
        <h2 className="text-[15px] font-semibold text-teal-900 mb-4">
          Leads by Month
        </h2>
        <div className="h-[280px] w-full">
          {!Array.isArray(leadsByMonth) || leadsByMonth.length === 0 ? (
            <div className="h-full flex items-center justify-center text-gray-400 text-sm">
              No chart data for this range
            </div>
          ) : (
            <Line data={chartData} options={chartOptions} />
          )}
        </div>
      </div>

      {/* Top tables 2x2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopTable
          title="Top Tags"
          nameHeader="Tag"
          nameKey="tag"
          rows={tables?.topTags ?? []}
          sort={tagSort}
          onSort={setTagSort}
          search={debouncedSearch}
        />
        <TopTable
          title="Top Categories"
          nameHeader="Category"
          nameKey="category"
          rows={tables?.topCategories ?? []}
          sort={catSort}
          onSort={setCatSort}
          search={debouncedSearch}
        />
        <TopTable
          title="Top Salespeople"
          nameHeader="Salesperson"
          nameKey="name"
          rows={tables?.topSalesPersons ?? []}
          getName={(r) => r.name || r.email || "—"}
          sort={spSort}
          onSort={setSpSort}
          search={debouncedSearch}
        />
        <TopTable
          title="Top Sources"
          nameHeader="Source"
          nameKey="source"
          rows={tables?.topSources ?? []}
          sort={srcSort}
          onSort={setSrcSort}
          search={debouncedSearch}
        />
      </div>
    </div>
  );
};

export default CrmDashboardView;
